import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';
import { queryKeys } from '@/lib/query-client.js';

export type MfaStatus = components['schemas']['MfaStatus'];
type Session = components['schemas']['Session'];

interface Authenticated {
  status: 'AUTHENTICATED';
  session: Session;
}

export function useMfaStatus(enabled = true) {
  return useQuery({
    queryKey: queryKeys.mfa(),
    queryFn: () => api.get<MfaStatus>('/auth/mfa'),
    enabled,
  });
}

/** Completes a sign-in that was waiting on a second factor. */
export function useVerifyMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { challengeId: string; code: string }) =>
      api.post<Authenticated>('/auth/mfa/verify', input),
    onSuccess: (result) => queryClient.setQueryData(queryKeys.session, result.session),
  });
}

export function useStartMfaEnrolment() {
  return useMutation({
    mutationFn: () => api.post<{ secret: string; otpauthUri: string }>('/auth/mfa/enrol/start'),
  });
}

export function useConfirmMfaEnrolment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { secret: string; code: string }) =>
      api.post<{ backupCodes: string[] }>('/auth/mfa/enrol/confirm', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mfa() });
      // The session carries `mfaEnrolmentRequired`, which has just changed.
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useRegenerateBackupCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ backupCodes: string[] }>('/auth/mfa/backup-codes'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.mfa() }),
  });
}

export function useDisableMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<{ success: true }>('/auth/mfa/disable', { code }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mfa() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}
